import { Collapse, Modal, Tabs, Tag, Typography } from "antd";
import { FC, ReactNode, useRef, useState } from "react";
import {
  IOperatorGuideEntry,
  OTHER_OPERATORS,
  QUESTION_OPERATORS,
  RESPONSE_OPERATORS,
} from "../utils/operatorGuideData";

const { Text, Paragraph } = Typography;

const SHORTCUTS: { trigger: string; label: string; description: string }[] = [
  {
    trigger: "@",
    label: "Question variables",
    description:
      "Insert a question variable, for QAND/QOR/QHASANSWER/QHASNOTANSWER.",
  },
  {
    trigger: "#",
    label: "Response variables",
    description: "Insert a response variable, for AND/OR/EQUAL/NOTEQUAL/COMPARE.",
  },
  {
    trigger: ":",
    label: "Operators",
    description: "Insert any operator name from this guide, pre-filled with ().",
  },
  {
    trigger: "$",
    label: "Ranks",
    description:
      "Insert another rank's name, for IMPORT — only shows ranks that are valid to import (excludes the current tab and any higher rank).",
  },
];

const MODAL_BODY_MAX_HEIGHT = 420;

// Matches a shortcut trigger character immediately followed by the word "shortcut" in an
// operator's description (e.g. "the # shortcut") — the trigger becomes a clickable link that
// jumps to and expands that trigger's entry in the Other tab's Typing shortcuts list.
const SHORTCUT_MENTION_PATTERN = /([$@#:]) shortcut/g;

const renderDescriptionWithShortcutLinks = (
  description: string,
  onShortcutClick: (trigger: string) => void,
): ReactNode[] => {
  const parts: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;

  SHORTCUT_MENTION_PATTERN.lastIndex = 0;
  while ((match = SHORTCUT_MENTION_PATTERN.exec(description))) {
    const trigger = match[1];
    if (match.index > lastIndex) {
      parts.push(description.slice(lastIndex, match.index));
    }
    parts.push(
      <Text
        key={`shortcut-link-${key++}`}
        className="operatorGuideShortcutLink"
        onClick={() => onShortcutClick(trigger)}
      >
        {trigger}
      </Text>,
    );
    parts.push(" shortcut");
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < description.length) {
    parts.push(description.slice(lastIndex));
  }
  return parts;
};

const ScrollableTabBody: FC<{ children: ReactNode }> = ({ children }) => (
  <div style={{ maxHeight: MODAL_BODY_MAX_HEIGHT, overflowY: "auto" }}>
    {children}
  </div>
);

const OperatorList: FC<{
  entries: IOperatorGuideEntry[];
  onShortcutClick: (trigger: string) => void;
}> = ({ entries, onShortcutClick }) => (
  <Collapse
    items={entries.map((entry) => ({
      key: entry.name,
      label: (
        <span>
          <Tag className="operatorTag">{entry.name}</Tag>
          <Text code className="operatorGuideSyntax">
            {entry.syntax}
          </Text>
        </span>
      ),
      children: (
        <>
          <Paragraph style={{ marginBottom: 8 }}>
            {renderDescriptionWithShortcutLinks(
              entry.description,
              onShortcutClick,
            )}
          </Paragraph>
          <div>
            <Text strong>
              {Array.isArray(entry.example) ? "Examples" : "Example"}
            </Text>
            {(Array.isArray(entry.example)
              ? entry.example
              : [entry.example]
            ).map((example, index) => (
              <Text code className="operatorGuideExample" key={index}>
                {example}
              </Text>
            ))}
          </div>
          {entry.notes && (
            <ul className="operatorGuideNotes">
              {entry.notes.map((note, index) => (
                <li key={index}>
                  <Text type="secondary">{note}</Text>
                </li>
              ))}
            </ul>
          )}
        </>
      ),
    }))}
  />
);

const OperatorGuideModal: FC<{ open: boolean; onClose: () => void }> = ({
  open,
  onClose,
}) => {
  const [activeTabKey, setActiveTabKey] = useState("response");
  const [openShortcutKeys, setOpenShortcutKeys] = useState<string[]>([]);
  const shortcutRefs = useRef<Record<string, HTMLElement | null>>({});

  const navigateToShortcut = (trigger: string) => {
    setActiveTabKey("other");
    setOpenShortcutKeys((prev) =>
      prev.includes(trigger) ? prev : [...prev, trigger],
    );
    setTimeout(() => {
      shortcutRefs.current[trigger]
        ?.closest(".ant-collapse-item")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  return (
    <Modal
      className="operatorGuideModal"
      title="Ranking Equation — Operator Guide"
      open={open}
      onCancel={onClose}
      onOk={onClose}
      cancelButtonProps={{ style: { display: "none" } }}
      okText="Close"
      width={720}
    >
      <div className="operatorGuideIntro">
        <Paragraph type="secondary">
          Every equation is built from these operators. Nest one inside
          another (e.g. <Text code>AND(OR(a, b), c)</Text>) to combine logic.
        </Paragraph>
      </div>
      <div className="operatorGuideIntro">
        <Paragraph type="secondary">
          Wrap a comparison <Text strong>value</Text> in double quotes — e.g.{" "}
          <Text code>EQUAL(var, "Judicial")</Text>,{" "}
          <Text code>COMPARE(var, {">"}, "18")</Text>. Quotes go around the
          value only — never around a response/question variable, a rank
          name, or an operator symbol.
        </Paragraph>
      </div>

      <Tabs
        activeKey={activeTabKey}
        onChange={setActiveTabKey}
        items={[
          {
            key: "response",
            label: "Response",
            forceRender: true,
            children: (
              <ScrollableTabBody>
                <OperatorList
                  entries={RESPONSE_OPERATORS}
                  onShortcutClick={navigateToShortcut}
                />
              </ScrollableTabBody>
            ),
          },
          {
            key: "question",
            label: "Question",
            forceRender: true,
            children: (
              <ScrollableTabBody>
                <OperatorList
                  entries={QUESTION_OPERATORS}
                  onShortcutClick={navigateToShortcut}
                />
              </ScrollableTabBody>
            ),
          },
          {
            key: "other",
            label: "Other",
            forceRender: true,
            children: (
              <ScrollableTabBody>
                <OperatorList
                  entries={OTHER_OPERATORS}
                  onShortcutClick={navigateToShortcut}
                />
                <Text strong className="operatorGuideShortcutsHeading">
                  Typing shortcuts
                </Text>
                <Collapse
                  activeKey={openShortcutKeys}
                  onChange={(keys) =>
                    setOpenShortcutKeys(
                      Array.isArray(keys) ? keys : [keys].filter(Boolean),
                    )
                  }
                  items={SHORTCUTS.map((shortcut) => ({
                    key: shortcut.trigger,
                    label: (
                      <span
                        ref={(el) => {
                          shortcutRefs.current[shortcut.trigger] = el;
                        }}
                      >
                        <Tag className="shortcutTag">{shortcut.trigger}</Tag>
                        <Text strong style={{ marginLeft: 5 }}>
                          {shortcut.label}
                        </Text>
                      </span>
                    ),
                    children: (
                      <Text type="secondary">{shortcut.description}</Text>
                    ),
                  }))}
                />
              </ScrollableTabBody>
            ),
          },
        ]}
      />
    </Modal>
  );
};

export default OperatorGuideModal;
